import type { FC, PropsWithChildren } from "react";
import { createContext, useRef } from "react";

export type PluginUrl = string;
export type PluginContextData = {
  loadScript: (url: PluginUrl) => Promise<void>;
};

export const PluginContext = createContext<null | PluginContextData>(null);

export const PluginContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const loadedScripts = useRef<Map<string, Promise<void>>>(new Map());

  const loadScript = async (pluginUrl: PluginUrl): Promise<void> => {
    // если скрипт уже грузится или загружен — возвращаем тот же Promise
    const existing = loadedScripts.current.get(pluginUrl);

    if (existing) return existing;

    const promise = new Promise<void>((resolve, reject) => {
      const container = ref.current;

      if (!container) {
        reject(new Error("Script container not found"));
        return;
      }

      // проверяем, не подключен ли уже тег <script> с таким src в DOM
      const existingScript = container.querySelector<HTMLScriptElement>(
        `script[src="${pluginUrl}"]`,
      );

      if (existingScript && existingScript.dataset.loaded === "true") {
        resolve();
        return;
      }

      const script = existingScript ?? document.createElement("script");
      script.src = pluginUrl;
      script.async = false; // важно: чтобы сохранить порядок исполнения
      script.type = "text/javascript";

      script.onload = () => {
        script.dataset.loaded = "true";
        resolve();
      };

      script.onerror = () => {
        loadedScripts.current.delete(pluginUrl);
        reject(new Error(`Failed to load script: ${pluginUrl}`));
      };

      if (!existingScript) container.appendChild(script);
    });

    loadedScripts.current.set(pluginUrl, promise);
    return promise;
  };

  return (
    <PluginContext.Provider value={{ loadScript }}>
      {children}
      <div ref={ref} id={"plugin-container"} />
    </PluginContext.Provider>
  );
};
